// One vocabulary drives speaking (numberToSpokenWords, spellDigitsInText) and
// re-parsing (extractNumericQuantities), so the grounding lint can never drift
// from what the formatter emits.
const ONES = [
    "zero",
    "one",
    "two",
    "three",
    "four",
    "five",
    "six",
    "seven",
    "eight",
    "nine",
    "ten",
    "eleven",
    "twelve",
    "thirteen",
    "fourteen",
    "fifteen",
    "sixteen",
    "seventeen",
    "eighteen",
    "nineteen",
];
const TENS = [
    "",
    "",
    "twenty",
    "thirty",
    "forty",
    "fifty",
    "sixty",
    "seventy",
    "eighty",
    "ninety",
];
const ORDINAL_ONES = [
    "",
    "first",
    "second",
    "third",
    "fourth",
    "fifth",
    "sixth",
    "seventh",
    "eighth",
    "ninth",
    "tenth",
    "eleventh",
    "twelfth",
    "thirteenth",
    "fourteenth",
    "fifteenth",
    "sixteenth",
    "seventeenth",
    "eighteenth",
    "nineteenth",
];
/** A spoken ordinal ("fourteenth", "twenty-first") for 1 to 99. */
export function spokenOrdinal(n) {
    if (n < 20)
        return ORDINAL_ONES[n] ?? "";
    const tens = TENS[Math.floor(n / 10)] ?? "";
    if (tens === "")
        return "";
    const rest = n % 10;
    return rest === 0 ? tens.replace(/y$/, "ieth") : `${tens}-${ORDINAL_ONES[rest]}`;
}
const SCALES = [
    ["million", 1_000_000],
    ["thousand", 1_000],
    ["hundred", 100],
];
function belowHundredWords(n) {
    if (n < 20)
        return ONES[n];
    const tens = TENS[Math.floor(n / 10)];
    const rest = n % 10;
    return rest === 0 ? tens : `${tens} ${ONES[rest]}`;
}
function belowThousandWords(n) {
    if (n < 100)
        return belowHundredWords(n);
    const hundreds = `${ONES[Math.floor(n / 100)]} hundred`;
    const rest = n % 100;
    return rest === 0 ? hundreds : `${hundreds} ${belowHundredWords(rest)}`;
}
function integerWords(n) {
    if (n === 0)
        return "zero";
    const parts = [];
    let rest = n;
    for (const [word, scale] of SCALES.slice(0, 2)) {
        if (rest >= scale) {
            parts.push(`${belowThousandWords(Math.floor(rest / scale))} ${word}`);
            rest %= scale;
        }
    }
    if (rest > 0)
        parts.push(belowThousandWords(rest));
    return parts.join(" ");
}
export function numberToSpokenWords(n) {
    if (!Number.isFinite(n))
        return String(n);
    const sign = n < 0 ? "minus " : "";
    const abs = Math.abs(n);
    const int = Math.trunc(abs);
    if (Number.isInteger(abs))
        return `${sign}${integerWords(int)}`;
    const frac = String(abs).split(".")[1] ?? "";
    if (frac === "5")
        return `${sign}${integerWords(int)} and a half`;
    const digits = frac
        .split("")
        .map((d) => ONES[Number(d)])
        .join(" ");
    return `${sign}${integerWords(int)} point ${digits}`;
}
const WORD_VALUES = new Map();
for (let i = 0; i < ONES.length; i++) {
    WORD_VALUES.set(ONES[i], { value: i, kind: i < 10 ? "unit" : "teen" });
}
for (let i = 2; i < TENS.length; i++) {
    WORD_VALUES.set(TENS[i], { value: i * 10, kind: "ten" });
}
for (const [word, value] of SCALES)
    WORD_VALUES.set(word, { value, kind: "scale" });
function freshParse() {
    return { total: 0, current: 0, fraction: 0, pointMode: false, pointScale: 0.1, started: false };
}
function closeParse(state, out) {
    if (!state.started)
        return;
    // Fraction accumulation is floating point ("point eight seven" walks to
    // 0.8700000000000001); normalise so a spoken value and its digit form
    // ("0.87") land on the same number for set membership.
    out.push(Math.round((state.total + state.current + state.fraction) * 1e9) / 1e9);
}
// Standard spoken-number grammar; an illegal continuation ("six forty") closes
// the running number and starts a new one, so clock-style phrases parse as
// their two spoken components.
function feedWord(state, word, out) {
    const entry = WORD_VALUES.get(word);
    if (!entry) {
        closeParse(state, out);
        return freshParse();
    }
    if (state.pointMode) {
        if (entry.kind === "unit" || entry.kind === "teen") {
            if (entry.value > 9) {
                closeParse(state, out);
                const next = freshParse();
                return feedWord(next, word, out);
            }
            state.fraction += entry.value * state.pointScale;
            state.pointScale /= 10;
            return state;
        }
        closeParse(state, out);
        return feedWord(freshParse(), word, out);
    }
    switch (entry.kind) {
        case "unit":
        case "teen": {
            const canAppendUnit = entry.kind === "unit" && state.current > 0 && state.current % 100 >= 20;
            if (state.started && !canAppendUnit && state.current % 100 !== 0 && state.current !== 0) {
                closeParse(state, out);
                return feedWord(freshParse(), word, out);
            }
            if (state.started && state.current === 0 && state.total === 0) {
                closeParse(state, out);
                return feedWord(freshParse(), word, out);
            }
            state.current += entry.value;
            state.started = true;
            return state;
        }
        case "ten": {
            if (state.started && state.current % 100 !== 0) {
                closeParse(state, out);
                return feedWord(freshParse(), word, out);
            }
            state.current += entry.value;
            state.started = true;
            return state;
        }
        case "scale": {
            const base = state.current === 0 ? 1 : state.current;
            if (entry.value === 100) {
                state.current = base * 100;
            }
            else {
                state.total += base * entry.value;
                state.current = 0;
            }
            state.started = true;
            return state;
        }
    }
}
const DIGIT_TOKEN = /(\d{1,2}):(\d{2})(?!\d)|\d[\d,]*(?:\.\d+)?/g;
/**
 * Every numeric quantity in a text, whether written as digits ("9,200",
 * "10:30", "6.5") or spelled the way the spoken vocabulary emits them
 * ("nine thousand two hundred", "ten thirty", "six and a half").
 */
export function extractNumericQuantities(text) {
    const out = [];
    for (const match of text.matchAll(DIGIT_TOKEN)) {
        if (match[1] !== undefined && match[2] !== undefined) {
            out.push(Number(match[1]), Number(match[2]));
        }
        else {
            out.push(Number(match[0].replaceAll(",", "")));
        }
    }
    const words = text
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .split(/\s+/)
        .filter(Boolean);
    let state = freshParse();
    for (let i = 0; i < words.length; i++) {
        const word = words[i];
        if (word === "point" && state.started && !state.pointMode) {
            state.pointMode = true;
            continue;
        }
        if (word === "and" && state.started && words[i + 1] === "a" && words[i + 2] === "half") {
            state.fraction += 0.5;
            closeParse(state, out);
            state = freshParse();
            i += 2;
            continue;
        }
        if (word === "and" && state.started && words[i + 1] === "a" && words[i + 2] === "quarter") {
            state.fraction += 0.25;
            closeParse(state, out);
            state = freshParse();
            i += 2;
            continue;
        }
        if (word === "and" &&
            state.started &&
            words[i + 1] === "three" &&
            (words[i + 2] === "quarters" || words[i + 2] === "quarter")) {
            state.fraction += 0.75;
            closeParse(state, out);
            state = freshParse();
            i += 2;
            continue;
        }
        if (word === "half" && !state.started) {
            out.push(0.5);
            continue;
        }
        if (word === "quarter" && !state.started) {
            out.push(0.25);
            continue;
        }
        if (word === "three" &&
            (words[i + 1] === "quarters" || words[i + 1] === "quarter") &&
            !state.started) {
            out.push(0.75);
            i += 1;
            continue;
        }
        state = feedWord(state, word, out);
    }
    closeParse(state, out);
    return out;
}
function clockMinuteWords(mm) {
    if (mm === 0)
        return "";
    if (mm < 10)
        return ` oh ${integerWords(mm)}`;
    return ` ${belowHundredWords(mm)}`;
}
const SPELL_TOKEN = /(\d{1,2}):(\d{2})(?!\d)|(\d[\d,]*(?:\.\d+)?)(\s*%)?/g;
/** Respell digit tokens as spoken words: "10:30" -> "ten thirty", "85%" -> "eighty five percent". */
export function spellDigitsInText(text) {
    return text.replace(SPELL_TOKEN, (whole, hh, mm, num, pct) => {
        if (hh !== undefined && mm !== undefined) {
            return `${integerWords(Number(hh))}${clockMinuteWords(Number(mm))}`;
        }
        if (num === undefined)
            return whole;
        const value = Number(String(num).replaceAll(",", ""));
        if (!Number.isFinite(value))
            return whole;
        return `${numberToSpokenWords(value)}${pct ? " percent" : ""}`;
    });
}
// Product-voice smalls ("one ask", "two minutes", "three breaths", "half an
// hour") are always allowed; flagging them would regen almost every draft.
export const BASE_ALLOWED_QUANTITIES = [0, 0.5, 1, 2, 3];
/**
 * The grounded quantity allow-set: the always-allowed smalls, any surface
 * extras (e.g. the snapshot's score denominators), and every quantity the
 * rendered prompt provides.
 */
export function allowedQuantitiesIn(promptText, extras = []) {
    const allowed = new Set([...BASE_ALLOWED_QUANTITIES, ...extras]);
    for (const value of extractNumericQuantities(promptText))
        allowed.add(value);
    return allowed;
}
/** Quantities in `text` that no input provides; same vocabulary as the formatter. */
export function ungroundedQuantities(text, allowed) {
    const out = [];
    for (const value of extractNumericQuantities(text)) {
        if (!allowed.has(value))
            out.push(value);
    }
    return out;
}
/** One violation line per ungrounded quantity in a named field. */
export function fieldQuantityViolations(field, text, allowed) {
    return ungroundedQuantities(text, allowed).map((value) => `${field} states "${value}" which no input provides`);
}
//# sourceMappingURL=spoken-numbers.js.map